import { v4 as uuid } from 'uuid';
import type {
  FormattingRule,
  PerspectivesFormatting as LegacyPerspectivesFormatting,
} from '@ardoq/perspectives';
import { PerspectivesFormatting } from '@ardoq/perspectives-sidebar';
import { CreateLabelOptions, filterInterface } from '@ardoq/filter-interface';
import { dispatchAction } from '@ardoq/rxbeach';
import { isSystemFilter, SystemFilterType } from '@ardoq/api-types';
import type { FilterAttributes } from '@ardoq/api-types';
import { pushGroupingAndFormattingRulesToHistory } from 'canvas/contextToolBar/loadedStateHistory/loadedStateHistoryActions';
import { referenceLabelFormattingChanged } from './actions';
import {
  DEFAULT_COMPONENT_LABEL_FORMATTING,
  DEFAULT_REFERENCE_LABEL_FORMATTING,
} from 'perspective/perspectiveEditor/labelFormattingUtils';
import {
  ReferenceLabelSource,
  type LabelFormattingInfo,
} from '@ardoq/data-model';
import { formattingRulesToFormattingAttributes } from '../../modelInterface/filters/formattingRulesToFormattingAttributes';

export const applyLabelFormatting = (
  labelFormatting: LabelFormattingInfo[],
  showReferenceType: boolean,
  transactionId?: string
) => {
  filterInterface.removeComponentLabelFormattingFilters(true);
  filterInterface.removeReferenceLabelFormattingFilters(true);

  if (showReferenceType) {
    filterInterface.createReferenceLabelFilter(
      ReferenceLabelSource.REFERENCE_TYPE,
      { transactionId }
    );
  } else {
    filterInterface.createReferenceLabelFilter(ReferenceLabelSource.NONE, {
      transactionId,
    });
  }

  filterInterface.createLabelFormatting(labelFormatting, transactionId);
};

export const applyFormatting = (
  formatting: PerspectivesFormatting | LegacyPerspectivesFormatting,
  transactionId?: string
) => {
  applyComponentLabelFormatting(formatting.componentLabelFormattingValue, {
    includeTime: formatting.isComponentLabelTimeIncluded,
    transactionId,
  });
  applyReferenceLabelFormatting(formatting.referenceLabelFormattingValue, {
    includeTime: formatting.isReferenceLabelTimeIncluded,
    transactionId,
  });
  applyConditionalFormatting(
    formatting.conditionalFormattingRules,
    true,
    transactionId
  );
};

export const applyConditionalFormatting = (
  conditionalFormattingRules?: FormattingRule[],
  shouldGreyOut: boolean = true,
  transactionId?: string
) => {
  if (!conditionalFormattingRules) {
    return;
  }

  filterInterface.setConditionalFormattingFilters(
    formattingRulesToFormattingAttributes(conditionalFormattingRules),
    shouldGreyOut,
    transactionId
  );
};

// Stamps the in-progress history entry with a shared transactionId so the
// rule mutation that follows lands as one undoable atom rather than being
// split. The empty rule arrays are placeholders — `formattingAndLabelRules$`
// will emit with this same transactionId once filterInterface mutates the
// Filters collection below, and `reduceFormattingAndLabelRulesUpdate`
// (currentHistoryOperations.ts) overwrites the rules in the same history
// entry when the transactionId matches. Same pattern as `applyPerspectiveData`
// in perspectiveEditorRoutines.ts.
export const applyConditionalFormattingAsOneHistoryEntry = (
  formattingAttributes: FilterAttributes[]
) => {
  const transactionId = uuid();
  dispatchAction(
    pushGroupingAndFormattingRulesToHistory({
      groupBys: { groupingRules: [], transactionId },
      conditionalFormatting: { rules: [], transactionId },
      transactionId,
    })
  );
  const shouldGreyOut = formattingAttributes.some(
    filter =>
      isSystemFilter(filter) && filter.name === SystemFilterType.DEFAULT_COLOR
  );
  filterInterface.setConditionalFormattingFilters(
    formattingAttributes,
    shouldGreyOut,
    transactionId
  );
};

export const applyLabelFormattingAsOneHistoryEntry = (
  labelFormatting: LabelFormattingInfo[],
  showReferenceType: boolean
): void => {
  const transactionId = uuid();
  dispatchAction(
    pushGroupingAndFormattingRulesToHistory({
      groupBys: { groupingRules: [], transactionId },
      conditionalFormatting: { rules: [], transactionId },
      transactionId,
    })
  );

  applyLabelFormatting(labelFormatting, showReferenceType, transactionId);
};

const applyComponentLabelFormatting = (
  componentLabelFormatting?: string,
  { includeTime, transactionId }: CreateLabelOptions = {}
) => {
  if (!componentLabelFormatting) {
    return;
  }

  const isDefaultFormatting =
    componentLabelFormatting === DEFAULT_COMPONENT_LABEL_FORMATTING;

  filterInterface.removeComponentLabelFormattingFilters(isDefaultFormatting);

  if (!isDefaultFormatting) {
    filterInterface.createComponentLabelFilter(componentLabelFormatting, {
      includeTime,
      transactionId,
    });
  }
};

const applyReferenceLabelFormatting = (
  referenceLabelFormatting?: string,
  { includeTime, transactionId }: CreateLabelOptions = {}
) => {
  if (!referenceLabelFormatting) {
    return;
  }

  const isSameFormattingAlreadySet =
    filterInterface.getReferenceLabelFilter().value ===
    referenceLabelFormatting;

  const isDefaultFormatting =
    referenceLabelFormatting === DEFAULT_REFERENCE_LABEL_FORMATTING;
  filterInterface.removeReferenceLabelFormattingFilters(isDefaultFormatting);

  if (!isDefaultFormatting) {
    filterInterface.createReferenceLabelFilter(referenceLabelFormatting, {
      includeTime,
      transactionId,
    });
  }

  if (!isSameFormattingAlreadySet) {
    dispatchAction(referenceLabelFormattingChanged(referenceLabelFormatting));
  }
};
